import React, { useMemo } from 'react';
import './AssessmentBarChart.css';

const DEFAULT_LABELS = {
    segments: {
        positive: 'Promoters',
        neutral: 'Passives',
        negative: 'Detractors'
    },
    formatDescription: (type, segments) => segments[type]
};

/**
 * AssessmentBarChart Component
 * Displays a segmented bar chart showing distribution of ratings with clickable segments
 * 
 * @param {Object} props
 * @param {Array} props.assessments - Array of post assessments containing rating values
 * @param {Object} props.thresholds - Object containing POSITIVE and NEGATIVE threshold values
 * @param {Function} props.onSegmentClick - Callback when segment is clicked, receives filtered posts and segment type
 * @param {Object} [props.labels] - Labels configuration
 * @param {Object} [props.labels.segments] - Labels for segments (positive/neutral/negative)
 * @param {Function} [props.labels.formatDescription] - Function to format description text (receives segment type and value)
 */
const AssessmentBarChart = ({ 
    assessments,
    thresholds,
    onSegmentClick,
    labels = {}
}) => {
    // Merge provided labels with defaults
    const mergedLabels = useMemo(() => ({
        segments: {
            ...DEFAULT_LABELS.segments,
            ...(labels.segments || {})
        },
        formatDescription: labels.formatDescription || DEFAULT_LABELS.formatDescription
    }), [labels]);

    // Calculate stats for each segment
    const stats = useMemo(() => {
        const result = {
            total: assessments.length,
            positive: 0,
            neutral: 0,
            negative: 0
        };

        assessments.forEach(assessment => {
            const rating = assessment.rating;
            if (rating > thresholds.POSITIVE) {
                result.positive++;
            } else if (rating < thresholds.NEGATIVE) {
                result.negative++;
            } else {
                result.neutral++;
            }
        });

        return result;
    }, [assessments, thresholds]);

    // Calculate and adjust percentages to ensure they sum to 100
    const percentages = useMemo(() => {
        if (stats.total === 0) {
            return {
                display: { pos: 0, neut: 0, neg: 0 },
                adjusted: { pos: 0, neut: 0, neg: 0 }
            };
        }

        const rawPercentages = {
            pos: Math.round((stats.positive / stats.total) * 100),
            neut: Math.round((stats.neutral / stats.total) * 100),
            neg: Math.round((stats.negative / stats.total) * 100)
        };

        const exactPercentages = {
            pos: (stats.positive / stats.total) * 100,
            neut: (stats.neutral / stats.total) * 100,
            neg: (stats.negative / stats.total) * 100
        };

        const roundingDiffs = {
            pos: Math.abs(exactPercentages.pos - rawPercentages.pos),
            neut: Math.abs(exactPercentages.neut - rawPercentages.neut),
            neg: Math.abs(exactPercentages.neg - rawPercentages.neg)
        };

        const totalPercentage = rawPercentages.pos + rawPercentages.neut + rawPercentages.neg;
        const diff = 100 - totalPercentage;

        // Find segment with largest rounding difference
        const segmentToAdjust = Object.entries(roundingDiffs)
            .sort(([, a], [, b]) => b - a)[0][0];

        return {
            display: rawPercentages,
            adjusted: {
                ...rawPercentages,
                [segmentToAdjust]: rawPercentages[segmentToAdjust] + diff
            }
        };
    }, [stats]);

    return (
        <div className="assessment-bar-chart">
            <div className="bar-container">
                {percentages.adjusted.pos > 0 && (
                    <div
                        className="bar-segment positive"
                        style={{ width: `${percentages.adjusted.pos}%` }}
                        onClick={() => onSegmentClick(
                            assessments.filter(a => a.rating > thresholds.POSITIVE),
                            'positive'
                        )}
                        title={`${percentages.display.pos}% ${mergedLabels.segments.positive}`}
                    />
                )}
                {percentages.adjusted.neut > 0 && (
                    <div
                        className="bar-segment neutral"
                        style={{ width: `${percentages.adjusted.neut}%` }}
                        onClick={() => onSegmentClick(
                            assessments.filter(a => a.rating >= thresholds.NEGATIVE && a.rating <= thresholds.POSITIVE),
                            'neutral'
                        )}
                        title={`${percentages.display.neut}% ${mergedLabels.segments.neutral}`}
                    />
                )}
                {percentages.adjusted.neg > 0 && (
                    <div
                        className="bar-segment negative"
                        style={{ width: `${percentages.adjusted.neg}%` }}
                        onClick={() => onSegmentClick(
                            assessments.filter(a => a.rating < thresholds.NEGATIVE),
                            'negative'
                        )}
                        title={`${percentages.display.neg}% ${mergedLabels.segments.negative}`}
                    />
                )}
            </div>
            
            <div className="legend">
                <div className="legend-item">
                    <span className="legend-label">
                        <span className="legend-percentage">{percentages.display.pos}%</span>
                        <span className="legend-description">
                            {mergedLabels.formatDescription('positive', mergedLabels.segments)}
                        </span>
                    </span>
                </div>
                {percentages.display.neut > 0 && (
                    <div className="legend-item">
                        <span className="legend-label">
                            <span className="legend-percentage">{percentages.display.neut}%</span>
                            <span className="legend-description">
                                {mergedLabels.formatDescription('neutral', mergedLabels.segments)}
                            </span>
                        </span>
                    </div>
                )}
                <div className="legend-item">
                    <span className="legend-label">
                        <span className="legend-percentage">{percentages.display.neg}%</span>
                        <span className="legend-description">
                            {mergedLabels.formatDescription('negative', mergedLabels.segments)}
                        </span>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default AssessmentBarChart; 